import { useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { keyBy } from "lodash";
import { useHover } from "usehooks-ts";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  ActiveTab,
  INHERIT_BUDGET_MIX_KPI,
  useMMMActiveTab,
  useMMMBudgetMixKpiId,
} from "@features/mmm/hooks/mmm-url-state";
import { useCacDataState } from "@features/mmm/hooks/use-cac-data-state";
import { useRoasDataState } from "@features/mmm/hooks/use-roas-data-state";
import {
  formatCurrencyExact,
  formatCurrencyToDollarAmount,
  formatPotentiallyInfiniteNumber,
  getCurrencySymbol,
  roundPotentiallyInfiniteNumber,
} from "@features/mmm/utils/formatters";
import { useFeatureFlags } from "@hooks/http";
import {
  calculateCac,
  calculateRoas,
  formatting,
  MMMClientState,
  type BudgetMixBreakdownSpendDatum,
  type BudgetMixDatumSummaryData,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useAdoptedMix } from "@pages/mmm/hooks/use-adopted-mix";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useClientSettingsColorMap } from "@pages/mmm/hooks/use-client-settings-color-map";
import { useLoadBudgetMixes } from "@pages/mmm/hooks/use-load-budget-mixes";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import Spinner from "@shared/spinner";

import { useMMMAdminMode } from "../budget-mix-table/utils";
import { KpiType } from "../client-setting/types";
import { useShowReferencePeriod } from "../hooks/use-show-reference-period";

import { CardWrapper } from "./card-wrapper";
import { ChartTableSection } from "./chart-table-section";
import { Footer } from "./footer";
import { MainMetricSummarySection } from "./main-metric-summary-section";
import { MetricBox, MetricHeader } from "./metric-box";
import {
  AdoptBudgetMixErrorModal,
  BudgetMixForecastModal,
  ChangeAdoptedMixModal,
  UnadoptBudgetMixModal,
} from "./modals";
import { PredictedRangeSection } from "./predicted-range-section";
import { ErrorTitleSection } from "./title-section/error-title-section";
import { RecommendedTitleSection } from "./title-section/recommended-title-section";
import { useBudgetMix } from "./use-budget-mix";

export enum BudgetMixCardType {
  HISTORICAL_MIX,
  RECOMMENDED_MIX,
}

export enum BudgetMixCardMode {
  CHART,
  TABLE,
}

export type HistoricalMetricComparisonData = {
  kpi_data?: Record<string, number>;
  spend?: number;
};

export type Props = {
  chartData: BudgetMixBreakdownSpendDatum[];
  summaryData: BudgetMixDatumSummaryData;
  mode: BudgetMixCardMode;
  budgetMixId: string;
  historicalComparisonData: HistoricalMetricComparisonData;
  isAdopted: boolean;
  clientSettingId?: string;
  mixIndex: number;
  budgetMix: FullBudgetMixResponse;
  dateRange: { startDate: string; endDate: string };
  loading: boolean;
};

export const BudgetMixCard = ({
  budgetMix,
  chartData,
  summaryData,
  mode,
  budgetMixId,
  historicalComparisonData,
  isAdopted,
  clientSettingId,
  mixIndex,
  dateRange,
  loading,
}: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const { kpi_data, spend } = summaryData;

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [mixToAdoptModalId, setMixToAdoptModalId] = useState<number | null>();
  const [mixToUnAdoptModalId, setMixToUnAdoptModalId] = useState<
    number | null
  >();
  const { selectedCycle } = useSelectedCycle();
  const [adoptedMixData] = useAdoptedMix();
  const [aboutToDelete, setAboutToDelete] = useState(false);
  const [isBudgetMixForecastModalOpen, setIsBudgetMixForecastModalOpen] =
    useState(false);

  const { clientSettings } = useClientSettings();

  const { selectedBudgetMixKpiId } = useMMMBudgetMixKpiId();
  const { kpis: clientKpis } = useClientKpis();
  const [cacDataState, setCacDataState] = useCacDataState();
  const [roasDataState, setRoasDataState] = useRoasDataState();
  const isAdminMode = useMMMAdminMode(user);
  const canBless = isAdminMode;

  const kpiByName = useMemo(() => keyBy(clientKpis, "kpi_name"), [clientKpis]);

  const clientSetting = useMemo(
    () => clientSettings.find((setting) => setting.id === clientSettingId),
    [clientSettings, clientSettingId],
  );
  const { kpi: inheritedClientSettingKpi } =
    useClientSettingData(clientSetting);

  const clientSettingsColorMap = useClientSettingsColorMap();
  const clientSettingColor = clientSettingsColorMap[clientSetting?.id || ""];
  const { setActiveTab } = useMMMActiveTab();

  const {
    blessBudgetMix,
    blessBudgetMixLoading,
    adoptBudgetMix,
    loadingAdoptBudgetMix,
    adoptError,
    unadoptBudgetMix,
    loadingUnAdoptBudgetMix,
    unadoptError,
    deleteBudgetMix,
    deleteBudgetMixLoading,
  } = useBudgetMix();

  const closeAdoptMixConfirmationModal = () => {
    trackMMMEvent({
      component:
        ExploreBudgetsTrackedComponent.CHANGE_ADOPTED_MIX_CONFIRMATION_MODAL,
      action: "onClose",
      actionInfo:
        "Choose not to change adopted budget mix (close adopt a mix modal)",
    });

    setMixToAdoptModalId(null);
    setMixToUnAdoptModalId(null);
  };

  const handleAdoptBudgetMixClick = async (id: number) => {
    if (adoptedMixData) {
      trackMMMEvent({
        component: ExploreBudgetsTrackedComponent.ADOPT_A_MIX_BUTTON,
        action: "onOpen",
        actionInfo:
          "Attempt to change adopted budget mix (open adopt a mix modal)",
      });

      setMixToAdoptModalId(id);
    } else {
      handleAdoptBudgetMix(id);
      closeAdoptMixConfirmationModal();
    }
  };

  const handleUnAdoptBudgetMixClick = async (id: number) => {
    if (adoptedMixData) {
      trackMMMEvent({
        component: ExploreBudgetsTrackedComponent.ADOPT_A_MIX_BUTTON,
        action: "onOpen",
        actionInfo:
          "Attempt to change adopted budget mix (open adopt a mix modal)",
      });

      setMixToUnAdoptModalId(id);
    } else {
      handleUnAdoptBudgetMix(id);
      closeAdoptMixConfirmationModal();
    }
  };

  const handleAdoptBudgetMix = async (id: number) => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.BUDGET_MIX_CARD,
      action: "onClick",
      actionInfo: "Adopt a budget mix",
    });

    try {
      await adoptBudgetMix({ id });

      if (adoptError) {
        alert("ERROR!");
        return;
      }
    } catch (err) {
      setIsErrorModalOpen(true);
      // TODO: handle with error from hook
    }
  };

  const handleUnAdoptBudgetMix = async (id: number) => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.BUDGET_MIX_CARD,
      action: "onClick",
      actionInfo: "Unadopt a budget mix",
    });

    try {
      await unadoptBudgetMix({ id });

      if (unadoptError) {
        alert("ERROR!");
        return;
      }
    } catch (err) {
      setIsErrorModalOpen(true);
      // TODO: handle with error from hook
    }
  };

  const { loadBudgetMixData, isBudgetMixDataLoading } = useLoadBudgetMixes();
  const handleConfirmAdoptBudgetMix = async (id: number) => {
    await handleAdoptBudgetMix(id);

    closeAdoptMixConfirmationModal();
    setActiveTab(ActiveTab.PERFORMANCE_TRACKING);
  };

  const handleConfirmUnAdoptBudgetMix = async (id: number) => {
    await handleUnAdoptBudgetMix(id);
    setMixToUnAdoptModalId(null);
  };
  const derivedBudgetMixKpi = useMemo(() => {
    if (selectedBudgetMixKpiId === INHERIT_BUDGET_MIX_KPI) {
      return (
        inheritedClientSettingKpi || kpiByName[budgetMix.data?.kpi_name ?? ""]
      );
    } else {
      return clientKpis.find(
        (kpi) => String(kpi.id) === String(selectedBudgetMixKpiId),
      );
    }
  }, [
    budgetMix,
    selectedBudgetMixKpiId,
    inheritedClientSettingKpi,
    kpiByName,
    clientKpis,
  ]);

  const predictedValues = useMemo(() => {
    const nameKey = derivedBudgetMixKpi?.kpi_name;
    if (nameKey) {
      return summaryData.kpi_data[nameKey]?.values;
    }
    return null;
  }, [summaryData, derivedBudgetMixKpi]);

  const cacOptions = useMemo(() => {
    const getCacText = (data: { low: number; high: number } | undefined) => {
      if (!data) {
        return "";
      }
      const { low, high } = data;
      const infFormatter = (inf: string) =>
        `${getCurrencySymbol(user.currencies.currentCurrency)}${inf}`;
      if (String(Math.round(low)).length > 1) {
        const formatter = (val: number) =>
          formatCurrencyToDollarAmount(val, user.currencies.currentCurrency);
        return `${formatPotentiallyInfiniteNumber(
          low,
          formatter,
          infFormatter,
        )} - ${formatPotentiallyInfiniteNumber(high, formatter, infFormatter)}`;
      }

      const formatter = (val: number) =>
        formatCurrencyExact(val, user.currencies.currentCurrency);
      return `${formatPotentiallyInfiniteNumber(
        low,
        formatter,
        infFormatter,
      )} - ${formatPotentiallyInfiniteNumber(high, formatter, infFormatter)}`;
    };
    const cacCompatibleKpis = clientKpis.filter(
      (kpi) => kpi.kpi_type === KpiType.TRANSACTIONS,
    );

    return [
      ...cacCompatibleKpis.map((kpi) => {
        const kpiValues =
          budgetMix.data.aggregated_data.summary_data.kpi_data[kpi.kpi_name]
            ?.values;
        if (!kpiValues) {
          return {
            key: kpi.id,
            displayLabel: kpi.friendly_name,
            displayValue: "",
            shouldShowAsOption: false,
            type: KpiType.TRANSACTIONS,
          };
        }
        const cacLow = calculateCac({ transactions: kpiValues.low, spend });
        const cacHigh = calculateCac({ transactions: kpiValues.high, spend });

        const contributions =
          budgetMix?.create_budget_recommendations_metadata
            ?.contribution_data?.[kpi.kpi_name];

        return {
          key: kpi.id,
          displayLabel: kpi.friendly_name,
          displayValue: (
            <div className="text-xs">
              <div>
                CAC:{" "}
                {formatting.formatCurrency(
                  calculateCac({ transactions: kpiValues.median, spend }),
                  {
                    precision: "approximate",
                    code: user.currencies.currentCurrency,
                  },
                )}{" "}
                | {getCacText({ low: cacLow, high: cacHigh })}
              </div>
              <div>
                iCAC:{" "}
                {contributions
                  ? formatting.formatCurrency(
                      calculateCac({
                        transactions:
                          (contributions.contribution_spend ?? 0) +
                          (contributions.contribution_spend_times_promo ?? 0),
                        spend,
                      }),
                      {
                        precision: "approximate",
                        code: user.currencies.currentCurrency,
                      },
                    )
                  : "--"}
              </div>
            </div>
          ),

          shouldShowAsOption: true,
          type: KpiType.TRANSACTIONS,
        };
      }),
      {
        key: INHERIT_BUDGET_MIX_KPI,
        displayLabel: "Not set",
        displayValue: "",
        shouldShowAsOption: false,
        type: KpiType.TRANSACTIONS,
      },
    ];
  }, [
    clientKpis,
    user.currencies.currentCurrency,
    budgetMix.data.aggregated_data.summary_data.kpi_data,
    budgetMix.create_budget_recommendations_metadata?.contribution_data,
    spend,
  ]);

  const selectedCacKpi = useMemo(() => {
    if (cacDataState === INHERIT_BUDGET_MIX_KPI) {
      const mixKpi =
        inheritedClientSettingKpi || kpiByName[budgetMix.data?.kpi_name ?? ""];
      if (mixKpi && mixKpi.kpi_type === KpiType.TRANSACTIONS) {
        return mixKpi.id;
      } else {
        return INHERIT_BUDGET_MIX_KPI;
      }
    }

    const cacKpi = clientKpis.find(
      (kpi) => String(kpi.id) === String(cacDataState),
    );
    if (cacKpi?.kpi_type === KpiType.TRANSACTIONS) {
      return cacDataState;
    }

    return INHERIT_BUDGET_MIX_KPI;
  }, [
    cacDataState,
    inheritedClientSettingKpi,
    kpiByName,
    budgetMix?.data.kpi_name,
    clientKpis,
  ]);

  const selectedRoasKpi = useMemo(() => {
    if (roasDataState === INHERIT_BUDGET_MIX_KPI) {
      const mixKpi =
        inheritedClientSettingKpi || kpiByName[budgetMix.data?.kpi_name ?? ""];
      if (mixKpi && mixKpi.kpi_type === KpiType.REVENUE) {
        return mixKpi.id;
      } else {
        return INHERIT_BUDGET_MIX_KPI;
      }
    }

    const roasKpi = clientKpis.find(
      (kpi) => String(kpi.id) === String(roasDataState),
    );
    if (roasKpi?.kpi_type === KpiType.REVENUE) {
      return roasDataState;
    }

    return INHERIT_BUDGET_MIX_KPI;
  }, [
    roasDataState,
    inheritedClientSettingKpi,
    kpiByName,
    budgetMix?.data.kpi_name,
    clientKpis,
  ]);

  const roasOptions = useMemo(() => {
    const getRoasText = (data: { low: number; high: number } | undefined) => {
      if (!data) {
        return "";
      }

      const { low, high } = data;
      return `${roundPotentiallyInfiniteNumber(
        low,
        2,
      )} - ${roundPotentiallyInfiniteNumber(high, 2)}x`;
    };

    const roasCompatibleKpis = clientKpis.filter(
      (kpi) => kpi.kpi_type === KpiType.REVENUE,
    );

    return [
      ...roasCompatibleKpis.map((kpi) => {
        const kpiValues =
          budgetMix.data.aggregated_data.summary_data.kpi_data[kpi.kpi_name]
            ?.values;
        if (!kpiValues) {
          return {
            key: kpi.id,
            displayLabel: kpi.friendly_name,
            displayValue: "",
            shouldShowAsOption: false,
            type: KpiType.REVENUE,
          };
        }
        const contributions =
          budgetMix?.create_budget_recommendations_metadata
            ?.contribution_data?.[kpi.kpi_name];
        const roasLow = calculateRoas({ revenue: kpiValues.low, spend });
        const roasHigh = calculateRoas({ revenue: kpiValues.high, spend });
        return {
          key: kpi.id,
          displayLabel: kpi.friendly_name,
          displayValue: (
            <div className="text-xs">
              <div>
                ROAS:{" "}
                {formatting.formatMultiplier(
                  calculateRoas({ revenue: kpiValues.median, spend }),
                  {
                    precision: "approximate",
                  },
                )}{" "}
                | {getRoasText({ low: roasLow, high: roasHigh })}
              </div>
              <div>
                iROAS:{" "}
                {contributions
                  ? formatting.formatMultiplier(
                      calculateRoas({
                        revenue:
                          (contributions.contribution_spend ?? 0) +
                          (contributions.contribution_spend_times_promo ?? 0),
                        spend,
                      }),
                      {
                        precision: "approximate",
                      },
                    )
                  : "--"}
              </div>
            </div>
          ),
          shouldShowAsOption: true,
          type: KpiType.REVENUE,
        };
      }),
      {
        key: INHERIT_BUDGET_MIX_KPI,
        displayLabel: "Not set",
        displayValue: "",
        shouldShowAsOption: false,
        type: KpiType.REVENUE,
      },
    ];
  }, [
    clientKpis,
    budgetMix.data.aggregated_data.summary_data.kpi_data,
    budgetMix.create_budget_recommendations_metadata?.contribution_data,
    spend,
  ]);

  const enableMmmReferencePeriod = useShowReferencePeriod();

  if (aboutToDelete) {
    return (
      <div className="h-full">
        <CardWrapper
          topBarColor="#FDEFEB"
          className="p-4 h-full justify-start bg-mars-2 hover:border-mars"
        >
          <div className="font-semibold mb-5">Delete budget?</div>
          <div className="flex flex-col justify-stretch gap-3 w-full">
            <PrimaryButton
              disabled={deleteBudgetMixLoading || isBudgetMixDataLoading}
              className={clsx(
                "font-bold flex items-center justify-center gap-2",
                {
                  "bg-mars text-white hover:bg-mars+1": !deleteBudgetMixLoading,
                },
              )}
              onClick={async () => {
                trackMMMEvent({
                  component:
                    ExploreBudgetsTrackedComponent.DELETE_BUDGET_MIX_CARD_BUTTON,
                  action: "onClick",
                  actionInfo: "Delete budget mix",
                });

                await deleteBudgetMix({ variables: { id: budgetMix.id } });
                await loadBudgetMixData();
              }}
            >
              {(deleteBudgetMixLoading || isBudgetMixDataLoading) && (
                <Spinner colorClassname="text-white-400" />
              )}
              YES, DELETE
            </PrimaryButton>
            <SecondaryButton
              disabled={deleteBudgetMixLoading || isBudgetMixDataLoading}
              className="font-bold"
              onClick={() => setAboutToDelete(false)}
            >
              NO, CANCEL
            </SecondaryButton>
          </div>
        </CardWrapper>
      </div>
    );
  }

  return (
    <>
      <CardWrapper topBarColor={clientSettingColor}>
        <AnimatePresence>
          {loading && (
            <motion.div
              className="absolute top-0 bottom-0 left-0 right-0 z-10 flex-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="absolute top-0 bottom-0 left-0 right-0 bg-v2-secondary opacity-60"></div>
              <img
                className="h-24 z-1"
                src={salesPlatformColumnLoading}
                alt="Loading table"
              />
            </motion.div>
          )}
        </AnimatePresence>
        {enableMmmReferencePeriod ? (
          <>
            {derivedBudgetMixKpi?.kpi_type === KpiType.TRANSACTIONS ? (
              <MetricHeader
                value={selectedCacKpi}
                options={cacOptions}
                historicalComparisonData={historicalComparisonData}
                clientSetting={clientSetting}
                budgetMix={budgetMix}
                dateRange={dateRange}
              />
            ) : (
              <MetricHeader
                value={selectedRoasKpi}
                options={roasOptions}
                historicalComparisonData={historicalComparisonData}
                clientSetting={clientSetting}
                budgetMix={budgetMix}
                dateRange={dateRange}
              />
            )}
          </>
        ) : null}
        {clientSetting || budgetMix.is_custom ? (
          <RecommendedTitleSection
            budgetMix={budgetMix}
            title={budgetMix.name ? budgetMix.name : `Budget ${mixIndex + 1}`}
            onClickDelete={() => setAboutToDelete(true)}
            openBudgetMixForecastModal={() =>
              setIsBudgetMixForecastModalOpen(true)
            }
            clientSettingColor={clientSettingColor}
          />
        ) : (
          <ErrorTitleSection
            historicalCompareData={historicalComparisonData}
            title={`Budget ${mixIndex + 1}`}
            kpiData={kpi_data}
            spend={budgetMix.data.aggregated_data.summary_data.spend}
          />
        )}
        <MainMetricSummarySection>
          {predictedValues && derivedBudgetMixKpi && (
            <PredictedRangeSection
              predictedValues={predictedValues}
              kpi={derivedBudgetMixKpi}
              budgetMixMetadata={
                budgetMix.create_budget_recommendations_metadata
              }
            />
          )}
        </MainMetricSummarySection>
        <div className="flex items-center w-full border-b border-v2-secondary">
          <MetricBox
            value="spend"
            className="border-r border-v2-secondary w-2/5 h-16"
            budgetMix={budgetMix}
            historicalComparisonData={historicalComparisonData}
            options={[
              {
                key: "spend",
                displayLabel: "Spend",
                displayValue: formatCurrencyToDollarAmount(
                  spend,
                  user.currencies.currentCurrency,
                ),
                shouldShowAsOption: true,
                type: "spend",
              },
            ]}
          />
          {derivedBudgetMixKpi?.kpi_type === KpiType.TRANSACTIONS ? (
            <MetricBox
              className="w-3/5 h-16"
              value={selectedCacKpi}
              options={cacOptions}
              clientSetting={clientSetting}
              budgetMix={budgetMix}
              dateRange={dateRange}
            />
          ) : (
            <MetricBox
              className="w-3/5 h-16"
              value={selectedRoasKpi}
              options={roasOptions}
              clientSetting={clientSetting}
              budgetMix={budgetMix}
              dateRange={dateRange}
            />
          )}
        </div>
        <ChartTableSection
          chartData={chartData}
          useChart={mode === BudgetMixCardMode.CHART}
          dateRange={dateRange}
        />
        <Footer>
          {canBless && (
            <>
              {blessBudgetMixLoading || isBudgetMixDataLoading ? (
                "Loading..."
              ) : (
                <Checkbox
                  label="Is Blessed"
                  className="mr-3"
                  isDisabled={blessBudgetMixLoading}
                  onChange={async () => {
                    trackMMMEvent({
                      component: ExploreBudgetsTrackedComponent.BUDGET_MIX_CARD,
                      action: "onClick",
                      actionInfo: "Bless budget mix",
                    });

                    await blessBudgetMix({
                      variables: {
                        id: budgetMix.id,
                        bless: !budgetMix.is_blessed,
                      },
                    });
                    await loadBudgetMixData();
                  }}
                  value={!!budgetMix.is_blessed}
                />
              )}
            </>
          )}
          {isAdopted ? (
            <AdoptedBudgetMixAdoptButton
              onClick={() =>
                handleUnAdoptBudgetMixClick(Number.parseInt(budgetMixId))
              }
              isLoading={loadingUnAdoptBudgetMix}
            />
          ) : (
            <UnadoptedBudgetMixAdoptButton
              onClick={() =>
                handleAdoptBudgetMixClick(Number.parseInt(budgetMixId))
              }
              isLoading={loadingAdoptBudgetMix}
            />
          )}
        </Footer>
      </CardWrapper>
      <ChangeAdoptedMixModal
        isOpen={!!mixToAdoptModalId}
        closeModal={closeAdoptMixConfirmationModal}
        mixToAdoptModalId={mixToAdoptModalId}
        handleConfirmAdoptBudgetMix={handleConfirmAdoptBudgetMix}
      />
      <UnadoptBudgetMixModal
        isOpen={!!mixToUnAdoptModalId}
        closeModal={closeAdoptMixConfirmationModal}
        mixToUnAdoptModalId={mixToUnAdoptModalId}
        handleConfirmUnAdoptBudgetMix={handleConfirmUnAdoptBudgetMix}
      />
      <AdoptBudgetMixErrorModal
        isOpen={isErrorModalOpen}
        closeModal={() => setIsErrorModalOpen(false)}
      />
      <BudgetMixForecastModal
        budgetMix={budgetMix}
        inheritedClientSettingKpi={inheritedClientSettingKpi}
        isOpen={isBudgetMixForecastModalOpen}
        closeModal={() => setIsBudgetMixForecastModalOpen(false)}
      />
    </>
  );
};

function AdoptedBudgetMixAdoptButton({
  isLoading,
  onClick,
}: {
  isLoading: boolean;
  onClick: () => void;
}) {
  const { selectedCycle } = useSelectedCycle();
  const canUnadopt = !(selectedCycle.state === MMMClientState.REVIEWING_PERIOD);

  const hoverRef = useRef(null);

  const isHover = useHover(hoverRef);

  return (
    <GenericTooltip
      noInfoCircle
      showTooltip={!canUnadopt}
      placement="top"
      content="Cannot unadopt a budget in the Tracking or Reviewing stage of the cycle"
    >
      <PrimaryButton
        className={clsx("bg-alien", {
          "hover:bg-primary": canUnadopt,
        })}
        disabled={isLoading || !canUnadopt}
        loading={isLoading}
        onClick={onClick}
        style={{ minWidth: "100%" }}
        ref={hoverRef}
      >
        <div className="flex justify-center items-center gap-2">
          <i className="fa-solid fa-check"></i>
          <span>{isHover && canUnadopt ? "UNADOPT" : "ADOPTED"}</span>
        </div>
      </PrimaryButton>
    </GenericTooltip>
  );
}

function UnadoptedBudgetMixAdoptButton({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) {
  return (
    <PrimaryButton
      onClick={onClick}
      loading={isLoading}
      style={{ minWidth: "100%" }}
    >
      ADOPT
    </PrimaryButton>
  );
}
